import { useCallback, useState, type ReactNode } from 'react';
import { DevIdentityPanel } from '@traceio-org/auth-ui';
import { PRODUCTS, type ProductKey } from '@traceio-org/platform-contracts';
import type { DecisionPlan, LoginNotice } from '../navigation/decision';
import { useHub } from './context';
import { useLoad } from './hooks';

/**
 * Signs in as a development fixture, records the session as active, then runs `afterSignIn` (normally
 * decide and follow). The fixture list stays disabled while the browser leaves for another origin.
 */
export function useDevSignIn(afterSignIn: () => Promise<DecisionPlan | null | void>) {
  const deps = useHub();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const signInAs = useCallback(
    async (fixtureKey: string) => {
      const dev = deps.sessions.dev;
      if (!dev) return;
      setBusyKey(fixtureKey);
      setError(null);
      try {
        await dev.signIn(fixtureKey);
      } catch (cause) {
        console.warn('account-hub: development sign-in failed', cause);
        setError('We could not start a development session. Check that platform-api is running, then try again.');
        setBusyKey(null);
        return;
      }
      deps.sessions.activate(dev);
      const plan = await afterSignIn();
      if (!plan || plan.type === 'navigate') setBusyKey(null);
    },
    [deps, afterSignIn],
  );

  return { busyKey, error, signInAs };
}

/** Loads fixtures when development identity is allowed in this build and the API reports it enabled. */
export function useDevFixtures(enabledByApi: boolean) {
  const { sessions } = useHub();
  const dev = sessions.dev;
  return useLoad(() => (dev && enabledByApi ? dev.listFixtures() : Promise.resolve([])), [dev, enabledByApi]);
}

/** Plain-words explanation of a reauthenticate decision that development identity cannot satisfy. */
export function devNoticeText(notice: LoginNotice, productKey: ProductKey | null): string {
  switch (notice.reason) {
    case 'sso_required':
      return notice.kind === 'reauthenticate' && notice.connectionHint
        ? `This workspace requires single sign-on through the ${notice.connectionHint} connection, and that identity signs in another way. Choose a development identity that uses ${notice.connectionHint}.`
        : 'This workspace requires its single sign-on connection, and that identity signs in another way. Choose a development identity that uses it.';
    case 'mfa_required':
      return `${productKey ? PRODUCTS[productKey].displayName : 'This product'} requires multi-factor authentication, and that development identity has not completed it. Choose one that has.`;
    case 'organization_login_required':
      return 'This workspace requires signing in through its organization. Choose a development identity that belongs to it.';
  }
}

interface DevSectionProps {
  /** The API reports development identity enabled (login options). */
  enabled: boolean;
  /**
   * Where no login options exist (the bare /login page, invitations), the fixtures call itself is the
   * API's report: the routes exist only when development identity is enabled. The panel then appears
   * only once the fixtures loaded.
   */
  probe?: boolean;
  afterSignIn: () => Promise<DecisionPlan | null | void>;
  notice?: ReactNode;
}

/** The development identity panel wired to the fixtures and the sign-in flow. */
export function DevSection({ enabled, probe = false, afterSignIn, notice }: DevSectionProps) {
  const { sessions } = useHub();
  // Never shown in staging or production builds, whatever the API says.
  const active = sessions.dev !== null && enabled;
  const [fixtures] = useDevFixtures(active);
  const { busyKey, error, signInAs } = useDevSignIn(afterSignIn);
  if (!active) return null;
  if (probe && fixtures.status !== 'ready') return null;
  const loadError = fixtures.status === 'error' ? 'We could not load the development identities.' : null;
  return (
    <DevIdentityPanel
      enabled
      fixtures={fixtures.status === 'ready' ? fixtures.data : []}
      loading={fixtures.status === 'loading'}
      onSelect={signInAs}
      busyKey={busyKey}
      error={error ?? loadError}
      notice={notice}
    />
  );
}
